import { DEVICE_TYPE_LAYOUT } from "../constants/deviceColumns";
import { parseJsonSafe } from "./json";
import { formatDecimal, formatTemperature } from "./formatters";
import { logger } from "./logger";

// Device types that are considered GPS trackers
const TRACKER_DEVICE_TYPES = [
  1, // Tracker
  4, // TrackerWithButton
  7, // StickerTracker
  14, // PetTracker
  15, // Oyster3
  16, // TrackerWithEvent
  17, // Tracker (was cayenneLPP)
  41, // DigitalMatterG62
  52, // Oyster3Fusion
  53, // T1000event
  56, // Oyster3FusionWithDoor
  58, // T1000package
  83, // T2000
  64, // Milesight EM400-TLD NB-IoT
  62, // WeIoT WiMass
];

/**
 * Checks if a device type is a tracker
 */
export function isTracker(deviceType) {
  if (
    !deviceType ||
    !DEVICE_TYPE_LAYOUT[deviceType] ||
    !DEVICE_TYPE_LAYOUT[deviceType].length
  ) {
    return false;
  }

  return DEVICE_TYPE_LAYOUT[deviceType][0].isTracker === true;
}

/**
 * Checks if a device is a GPS tracker type
 * @param {Object} device - The device object
 * @returns {boolean} - True if device is a tracker type
 */
export function isTrackerDevice(device) {
  if (!device || !device.device_type) {
    return false;
  }

  return TRACKER_DEVICE_TYPES.includes(parseInt(device.device_type));
}

/**
 * Checks if device history contains GPS coordinates
 * @param {Array} deviceHistory - Array of device history records
 * @returns {boolean} - True if history contains GPS data
 */
export function hasGPSData(deviceHistory) {
  if (!Array.isArray(deviceHistory) || deviceHistory.length === 0) {
    return false;
  }

  return deviceHistory.some((row) => {
    let data = row.data;

    // Parse JSON data if it's a string
    if (typeof data === "string") {
      data = parseJsonSafe(data, null);
      if (!data) {
        return false;
      }
    }

    // Check for GPS coordinates
    const lat = data.latitude || data.lat || data.gps_lat;
    const lng = data.longitude || data.lng || data.lon || data.gps_lng;

    return lat && lng && lat !== 0 && lng !== 0;
  });
}

// WeakMap cache for parsed device data - garbage collected when record is no longer referenced
const parsedDataCache = new WeakMap();
const parsedDeviceLastDataCache = new WeakMap();

/**
 * Gets the last device history record and parses its data
 * Performance optimized with WeakMap caching to avoid re-parsing the same JSON
 * @param {Array} deviceHistory - Array of device history records
 * @returns {Object|null} - Parsed data from the last record, or null if no history
 * @example
 * const lastData = getLastDeviceData(deviceHistory);
 * if (lastData) {
 *   console.log(lastData.button_pressed);
 * }
 */
export function getLastDeviceData(deviceHistory) {
  if (!Array.isArray(deviceHistory) || deviceHistory.length === 0) {
    return null;
  }

  try {
    const lastRecord = deviceHistory[deviceHistory.length - 1];

    // Check cache first to avoid re-parsing
    if (parsedDataCache.has(lastRecord)) {
      return parsedDataCache.get(lastRecord);
    }

    const parsedData = parseJsonSafe(lastRecord.data, null);
    if (!parsedData) {
      return null;
    }

    // Cache the result for future calls
    parsedDataCache.set(lastRecord, parsedData);

    return parsedData;
  } catch (error) {
    logger.error("Error parsing device history data:", error);
    return null;
  }
}

/**
 * Gets parsed last_data payload from a device object
 * Performance optimized with WeakMap caching to avoid re-parsing unchanged payloads
 * @param {Object} device - Device object that may contain last_data
 * @param {*} fallback - Fallback value when data is unavailable/invalid (default: null)
 * @returns {Object|*} - Parsed last_data payload or fallback
 */
export function getDeviceLastData(device, fallback = null) {
  if (!device || !device.last_data) {
    return fallback;
  }

  try {
    const cached = parsedDeviceLastDataCache.get(device);

    if (cached && cached.raw === device.last_data) {
      return cached.parsed ?? fallback;
    }

    const parsedData = parseJsonSafe(device.last_data, null);

    parsedDeviceLastDataCache.set(device, {
      raw: device.last_data,
      parsed: parsedData,
    });

    return parsedData ?? fallback;
  } catch (error) {
    logger.error("Error parsing device last_data:", error);
    return fallback;
  }
}

/**
 * Gets battery millivolts from parsed data and/or device object using common field fallbacks
 * @param {Object|null} data - Parsed payload object (e.g. last_data)
 * @param {Object|null} device - Device object fallback source
 * @param {*} fallback - Fallback value when no battery field is found
 * @returns {*}
 */
export function getBatteryMillivolts(data, device = null, fallback = null) {
  const dataBatteryMv =
    data?.voltageMv ??
    data?.batteryMv ??
    data?.bat ??
    data?.voltage_mv ??
    data?.battery_mv;

  if (dataBatteryMv !== undefined && dataBatteryMv !== null) {
    return dataBatteryMv;
  }

  const deviceBatteryMv =
    device?.batteryMv ??
    device?.voltageMv ??
    device?.battery_mv ??
    device?.voltage_mv;

  if (deviceBatteryMv !== undefined && deviceBatteryMv !== null) {
    return deviceBatteryMv;
  }

  return fallback;
}

/**
 * Gets the most relevant freshness timestamp for a device.
 * Prefers last_heard for "last seen"/online state, with legacy fallbacks.
 * @param {Object|null} device - Device object
 * @param {*} fallback - Fallback value when no timestamp is available
 * @returns {*}
 */
export function getDeviceFreshnessTime(device, fallback = null) {
  if (!device) {
    return fallback;
  }

  return device.last_heard ?? fallback;
}

/**
 * Gets a specific field value from the last device history record
 * @param {Array} deviceHistory - Array of device history records
 * @param {string} field - Field name to extract from parsed data
 * @param {*} defaultValue - Default value if field not found (default: null)
 * @returns {*} - Field value or default value
 * @example
 * const buttonPressed = getLastDeviceValue(deviceHistory, 'button_pressed', false);
 * const temperature = getLastDeviceValue(deviceHistory, 'temperature', 'N/A');
 */
export function getLastDeviceValue(deviceHistory, field, defaultValue = null) {
  const data = getLastDeviceData(deviceHistory);
  if (!data) {
    return defaultValue;
  }

  return data[field] !== undefined ? data[field] : defaultValue;
}

/**
 * Checks whether the last frame contains button press data
 * @param {Array} deviceHistory - Array of device history records
 * @returns {boolean} - True if the latest record has button_pressed state (0/1)
 */
export function hasButtonPressData(deviceHistory) {
  const buttonPressed = getLastDeviceValue(
    deviceHistory,
    "button_pressed",
    undefined
  );

  return (
    buttonPressed === 0 ||
    buttonPressed === 1 ||
    buttonPressed === "0" ||
    buttonPressed === "1"
  );
}

/**
 * Returns layout column width used by button-capable device types
 * @param {boolean} buttonPressed - Whether button_pressed data exists
 * @param {boolean} isShare - Whether page is in share mode
 * @returns {number} md column size
 */
export function getButtonPressColumn(buttonPressed, isShare) {
  if (isShare) {
    return buttonPressed ? 4 : 6;
  }

  return buttonPressed ? 3 : 4;
}

/**
 * Checks if a device is active (heard from within the last 48 hours)
 * This function is pure and avoids Date.now() issues in React rendering
 * @param {string} freshnessTime - The device's freshness timestamp
 * @returns {boolean} - True if device is active
 */
export function isDeviceActive(freshnessTime) {
  const ACTIVE_THRESHOLD_MS = 172800000; // 48 hours
  if (!freshnessTime) {
    return false;
  }

  const freshnessTimeMs = new Date(freshnessTime).getTime();
  if (Number.isNaN(freshnessTimeMs)) {
    return false;
  }

  const now = Date.now();
  return freshnessTimeMs >= now - ACTIVE_THRESHOLD_MS;
}

export function getFormattedContent(field, value) {
  if (value === undefined || value === null) return "N/A";

  if (
    field.includes("temperature") ||
    field === "temp" ||
    field.includes("temp_")
  ) {
    return formatTemperature(value, true);
  } else if (
    field.includes("humidity") ||
    field === "RH" ||
    field === "rh" ||
    field === "soil_moisture" ||
    field === "leaf_moisture" ||
    field === "moisture"
  ) {
    return formatDecimal(value) + "%";
  } else if (field === "co2" || field === "CO2") {
    return Array.isArray(value)
      ? formatDecimal(value[0]) + "ppm"
      : formatDecimal(value) + "ppm";
  } else if (field === "distance") {
    return formatDecimal(value) + "mm";
  } else if (field === "tvoc" || field === "TVOC") {
    return Array.isArray(value)
      ? formatDecimal(value[0]) + "ppb"
      : formatDecimal(value) + "ppb";
  } else if (field.includes("current")) {
    return formatDecimal(value) + "A";
  } else if (field.includes("voltage")) {
    return formatDecimal(value) + "V";
  } else if (field.includes("watt") || field === "watts") {
    return formatDecimal(value) + "W";
  } else if (field === "energy") {
    return formatDecimal(value) + "kWh";
  } else if (field === "time") {
    return formatDecimal(value) + "s";
  } else if (
    field.includes("pm") ||
    field === "PM2_5" ||
    field === "mass_conc_pm1_0" ||
    field === "mass_conc_pm2_5" ||
    field === "mass_conc_pm10"
  ) {
    return Array.isArray(value)
      ? formatDecimal(value[0]) + "µg/m³"
      : formatDecimal(value) + "µg/m³";
  } else if (field === "gas_pressure_psi") {
    return formatDecimal(value) + "psi";
  } else if (field === "idc_intput_ma") {
    return formatDecimal(value) + "mA";
  } else if (
    field === "door_open" ||
    field === "door_open2" ||
    field === "water_leak" ||
    field === "state"
  ) {
    return value === 1 || value === true ? "Yes" : "No";
  } else if (field === "sos_event") {
    return value === 1 || value === true ? "Yes" : "No";
  } else if (field === "motion") {
    return value ? "Yes" : "No";
  } else if (field === "leak") {
    return value === 1 || value === true ? "Yes" : "No";
  } else if (field === "isLost") {
    return value ? "Yes" : "No";
  } else if (field === "ignition") {
    return value ? "On" : "Off";
  } else if (field === "plug_state") {
    return value ? "On" : "Off";
  } else if (field === "alarm") {
    return value ? "Yes" : "No";
  } else if (field === "leak_status") {
    return value === "NoLeak" ? "No Leak" : value;
  } else if (field.startsWith("B") && !isNaN(field.substring(1))) {
    return value === 1 ? "Pressed" : "Not Pressed";
  } else if (field === "AQI" || field === "CO2" || field === "TVOC") {
    return Array.isArray(value)
      ? formatDecimal(value[0])
      : formatDecimal(value);
  } else if (field === "package_opened") {
    return value ? "Opened" : "Closed";
  }

  if (typeof value === "number") {
    return formatDecimal(value);
  }

  return value;
}
